import type { PriceValue } from "@anaconda/core/price/types"
import type { UpdateHighlighterProps } from "@rarible_int/anaconda_ui_components"

export type PriceFormatter = { format: (value: number) => string }

export const formatPrice = (value: number, formatter?: PriceFormatter) =>
  formatter ? formatter.format(value) : getPriceFormatter(value).format(value)

export const getPriceFormatter = (value: number): PriceFormatter => {
  if (value === 0) return intFormatter
  if (value < 0.001) return smallPriceFormatter
  if (value < 1) return maximum3FractionDigitsFormatter
  if (value < 100) return maximum2FractionDigitsFormatter
  if (value < 1000) return maximum1FractionDigitsFormatter
  if (value < 10000) return intFormatter

  return compactFormatter
}

const smallPriceFormatter = {
  format: () => "<0.001",
}

export const maximum4FractionDigitsFormatter = new Intl.NumberFormat("en-US", {
  maximumFractionDigits: 4,
})

export const maximum3FractionDigitsFormatter = new Intl.NumberFormat("en-US", {
  maximumFractionDigits: 3,
})

export const maximum2FractionDigitsFormatter = new Intl.NumberFormat("en-US", {
  maximumFractionDigits: 2,
})

export const maximum1FractionDigitsFormatter = new Intl.NumberFormat("en-US", {
  maximumFractionDigits: 1,
})

export const intFormatter = new Intl.NumberFormat("en-US", {
  maximumFractionDigits: 0,
})

export const compactFormatter = new Intl.NumberFormat("en-US", {
  maximumFractionDigits: 1,
  maximumSignificantDigits: 3,
  notation: "compact",
})

export const getPriceFormat = (
  value: number,
): UpdateHighlighterProps["format"] => {
  if (value === 0) return { maximumFractionDigits: 0 }
  if (value < 0.001) return { maximumFractionDigits: 4 }
  if (value < 1) return { maximumFractionDigits: 3 }
  if (value < 100) return { maximumFractionDigits: 2 }
  if (value < 1000) return { maximumFractionDigits: 1 }
  if (value < 10000) return { maximumFractionDigits: 0 }
  return {
    maximumFractionDigits: 1,
    maximumSignificantDigits: 3,
    notation: "compact",
  }
}

export function priceInCryptoAsText(price: PriceValue | undefined) {
  if (!price) return ""
  const { amount, currency } = price
  const formattedAmount = formatPrice(Number(amount))

  return `${formattedAmount} ${currency.abbreviation}`
}
